import * as React from 'react';

import { Link2 } from 'lucide-react';
import { useRouter } from 'next/router';

import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import { Stack } from '@mui/system';

import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonTextPrimary,
} from '@itp/talent/components/core/button';
import { PasswordField } from '@itp/talent/components/core/passwordField';
import { TextField } from '@itp/talent/components/core/textField';
import { Form } from '@itp/talent/components/form';
import { PRIVATE_API_URL } from '@itp/talent/constants/config';
import { useLocalAuth } from '@itp/talent/context/localAuthContext';

import { Auth0Icon } from '../components/auth0Icon';
import { AuthCard } from '../components/authCard';
import { AuthDivider } from '../components/authDivider';
import { AuthFooterLinks } from '../components/authFooterLinks';
import { AuthHeading } from '../components/authHeading';
import { AuthSubheading } from '../components/authSubheading';
import { SocialMediaButtons } from '../components/socialMediaButtons';
import { useAuthRedirectGuard } from '../components/useAuthRedirectGuard';
import { MagicLinkSchema, SignInFormSchema } from './signInPage.schema';

type PasswordFormValues = {
  email: string;
  password: string;
};

type MagicLinkFormValues = {
  email: string;
};

type AuthMode = 'magic-link' | 'password';

const SIGN_IN_DEFAULTS: PasswordFormValues = { email: '', password: '' };
const MAGIC_LINK_DEFAULTS: MagicLinkFormValues = { email: '' };

export const SignInPage: React.FC = () => {
  const { isPageReady } = useAuthRedirectGuard();

  const router = useRouter();
  const { loginWithRedirect } = useAuth0();
  const { setTokens } = useLocalAuth();

  const [serverError, setServerError] = React.useState<null | string>(null);
  const [authMode, setAuthMode] = React.useState<AuthMode>('password');
  const [magicLinkSent, setMagicLinkSent] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = React.useState<null | string>(null);
  const [resendSent, setResendSent] = React.useState(false);

  const onSubmit = async (values: PasswordFormValues) => {
    if (isSubmitting) return;

    setServerError(null);
    setUnverifiedEmail(null);
    setResendSent(false);
    setIsSubmitting(true);

    try {
      const res = await fetch(`${PRIVATE_API_URL}/auth/local/login`, {
        body: JSON.stringify({ email: values.email, password: values.password }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403 && data?.error === 'email_not_verified') {
          setUnverifiedEmail(values.email);
        } else {
          setServerError(data?.error ?? 'Login failed');
        }
        return;
      }

      setTokens(data.token, data.refreshToken);
      router.replace('/');
    } catch {
      setServerError('Network error, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResendVerification = async () => {
    if (!unverifiedEmail || isSubmitting) return;

    setIsSubmitting(true);
    setResendSent(false);

    try {
      await fetch(`${PRIVATE_API_URL}/auth/local/resend-verification`, {
        body: JSON.stringify({ email: unverifiedEmail }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      setResendSent(true);
    } catch {
      setServerError('Network error, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onMagicLinkSubmit = async (values: MagicLinkFormValues) => {
    if (isSubmitting) return;

    setServerError(null);
    setIsSubmitting(true);

    try {
      const res = await fetch(`${PRIVATE_API_URL}/auth/magic/request`, {
        body: JSON.stringify({ email: values.email }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (!res.ok) {
        const data = await res.json();
        setServerError(data?.error ?? 'Failed to send magic link');
        return;
      }

      setMagicLinkSent(true);
    } catch {
      setServerError('Network error, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchMode = (mode: AuthMode) => {
    setServerError(null);
    setMagicLinkSent(false);
    setUnverifiedEmail(null);
    setResendSent(false);
    setAuthMode(mode);
  };

  if (!isPageReady) return null;

  return (
    <AuthCard>
      <AuthHeading>Log in</AuthHeading>
      <AuthSubheading variant="body2">Choose password or magic link sign-in.</AuthSubheading>

      {authMode === 'password' && !unverifiedEmail && (
        <Form
          defaultValues={SIGN_IN_DEFAULTS}
          error={serverError ? { message: serverError, name: 'email', type: 'manual' } : undefined}
          id="signInForm"
          onSubmit={onSubmit}
          schema={SignInFormSchema}
        >
          <Stack gap={3}>
            <TextField
              autoComplete="email"
              id="email"
              label="Email address"
              name="email"
              required
            />
            <PasswordField
              autoComplete="current-password"
              id="password"
              label="Password"
              name="password"
              required
            />

            <ButtonPrimary disabled={isSubmitting} type="submit">
              {isSubmitting ? 'Logging in…' : 'Login'}
            </ButtonPrimary>

            <ButtonTextPrimary
              disabled={isSubmitting}
              onClick={() => handleSwitchMode('magic-link')}
              sx={{ gap: 1 }}
            >
              <Link2 size={16} />
              Sign in with magic link
            </ButtonTextPrimary>
          </Stack>
        </Form>
      )}

      {authMode === 'password' && unverifiedEmail && (
        <Stack gap={2}>
          <Typography color="text.secondary" textAlign="center" variant="body2">
            Your email address has not been verified. Check your inbox or resend the link.
          </Typography>
          {resendSent ? (
            <Typography color="text.secondary" textAlign="center" variant="body2">
              Verification email sent to <b>{unverifiedEmail}</b>.
            </Typography>
          ) : (
            <ButtonPrimary disabled={isSubmitting} onClick={onResendVerification}>
              {isSubmitting ? 'Sending…' : 'Resend verification email'}
            </ButtonPrimary>
          )}
          <ButtonTextPrimary onClick={() => setUnverifiedEmail(null)}>Back</ButtonTextPrimary>
        </Stack>
      )}

      {authMode === 'magic-link' && !magicLinkSent && (
        <Form
          defaultValues={MAGIC_LINK_DEFAULTS}
          error={serverError ? { message: serverError, name: 'email', type: 'manual' } : undefined}
          id="magicLinkForm"
          onSubmit={onMagicLinkSubmit}
          schema={MagicLinkSchema}
        >
          <Stack gap={3}>
            <TextField
              autoComplete="email"
              id="email"
              label="Email address"
              name="email"
              required
            />

            <ButtonPrimary disabled={isSubmitting} type="submit">
              {isSubmitting ? 'Sending…' : 'Send magic link'}
            </ButtonPrimary>

            <ButtonTextPrimary disabled={isSubmitting} onClick={() => handleSwitchMode('password')}>
              Sign in with password
            </ButtonTextPrimary>
          </Stack>
        </Form>
      )}

      {authMode === 'magic-link' && magicLinkSent && (
        <Stack gap={2}>
          <Typography color="text.secondary" textAlign="center" variant="body2">
            Check your inbox — a sign-in link has been sent.
          </Typography>
          <ButtonTextPrimary onClick={() => handleSwitchMode('password')}>
            Sign in with password
          </ButtonTextPrimary>
        </Stack>
      )}

      <AuthDivider />

      <SocialMediaButtons>
        <ButtonSecondary onClick={() => loginWithRedirect()} sx={{ gap: 1 }} type="button">
          <Auth0Icon />
        </ButtonSecondary>
      </SocialMediaButtons>

      <AuthDivider />

      <AuthFooterLinks
        leftAction={{ href: '/sign-up', label: 'Create account' }}
        rightAction={{ href: '/forgot-password', label: 'Forgot password?' }}
      />
    </AuthCard>
  );
};
